import { BRAND_NAME } from "@cms/config";
import React from "react";
import {
  LayoutDashboard,
  FileText,
  Menu,
  Images,
  Settings,
  Inbox,
  CreditCard,
  UtensilsCrossed,
  CalendarClock,
  CalendarDays,
  LifeBuoy,
  Newspaper,
  Puzzle,
  ShieldAlert,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  SidebarShell,
  SidebarNavItem,
  SidebarGroupLabel,
  SidebarEnvIndicator,
  SidebarUserMenu,
} from "@cms/ui/components/sidebar";
import { CLIENT_PORTAL_NAV } from "@cms/portal-lib/client-portal-nav";
import { activeModuleNav } from "../lib/modules";
import type { Impersonation, PortalUser } from "../lib/outlet-context";

// The static page list is shared with the Admin Portal (portal-lib); only the
// icons are presentation-local, keyed by href.
const navIcons: Record<string, LucideIcon> = {
  "/": LayoutDashboard,
  "/pages": FileText,
  "/media": Images,
  "/menus": Menu,
  "/forms": Inbox,
  "/modules": Puzzle,
  "/billing": CreditCard,
  "/settings": Settings,
};

const navGroup = (group: "root" | "content" | "website") =>
  CLIENT_PORTAL_NAV.filter((page) => page.group === group).map((page) => ({
    label: page.label,
    href: page.href,
    icon: navIcons[page.href] ?? FileText,
  }));

const contentNav = navGroup("content");
const websiteNav = navGroup("website");

// Keyed by the nav item's unique key.
const moduleIcons: Record<string, LucideIcon> = {
  restaurant_menu: UtensilsCrossed,
  booking: CalendarClock,
  posts: Newspaper,
  events: CalendarDays,
  sla: LifeBuoy,
};

// Replaces the UserMenu slot when an admin is managing this client's site on
// their behalf — makes the impersonated session unmistakable at a glance.
function AdminSessionBadge({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-md bg-warning px-2.5 py-2 text-warning-foreground">
      <ShieldAlert className="h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase leading-none tracking-wide">Admin session</p>
        <p className="mt-1 truncate text-sm font-semibold leading-none">{label}</p>
      </div>
    </div>
  );
}

export function Sidebar({ activeModules = [], unreadForms = 0, user, environment, impersonation, onSignOut }: {
  activeModules?: string[];
  unreadForms?: number;
  user: PortalUser | null;
  environment?: string;
  impersonation: Impersonation | null;
  onSignOut: () => void;
}) {
  // Modules stay fully hidden unless active — no locked or greyed-out items.
  const moduleNav = activeModuleNav(activeModules);

  return (
    <SidebarShell
      title={BRAND_NAME}
      subtitle="Client Portal"
      footer={
        <>
          <SidebarEnvIndicator environment={environment} />
          {impersonation ? (
            <AdminSessionBadge label={impersonation.label} />
          ) : (
            <SidebarUserMenu email={user?.email || ""} accountHref="/account" onSignOut={onSignOut} />
          )}
        </>
      }
    >
      <div className="space-y-0.5">
        <SidebarNavItem href="/" icon={LayoutDashboard} label="Dashboard" exact />
      </div>

      <div className="mt-4 space-y-0.5">
        <SidebarGroupLabel>Content</SidebarGroupLabel>
        {contentNav.map((item) => (
          <SidebarNavItem key={item.href} {...item} />
        ))}
      </div>

      <div className="mt-4 space-y-0.5">
        <SidebarGroupLabel>Website</SidebarGroupLabel>
        {websiteNav.map((item) => (
          <SidebarNavItem
            key={item.href}
            {...item}
            badge={item.href === "/forms" ? unreadForms : 0}
          />
        ))}
      </div>

      {moduleNav.length > 0 && (
        <div className="mt-4 space-y-0.5">
          <SidebarGroupLabel>Add-ons</SidebarGroupLabel>
          {moduleNav.map(({ key, label, href }) => (
            // Non-exact match: the Menu entry stays lit across its tabs
            // (/menu, /menu/items, /menu/qr) and the per-menu editor.
            <SidebarNavItem
              key={key}
              href={href}
              icon={moduleIcons[key] ?? FileText}
              label={label}
            />
          ))}
        </div>
      )}
    </SidebarShell>
  );
}
